import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  TrackByFunction,
} from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { TableViewCellDirective } from './table-view-cell.directive';
import { TableViewService } from './table-view.service';

@Component({
  selector: 'osn-cxb-table-view',
  templateUrl: './table-view.component.html',
  styleUrls: ['./table-view.component.scss'],
  providers: [TableViewService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableViewComponent<T> implements AfterContentInit, OnChanges {
  public dataSource$: BehaviorSubject<T[]> = new BehaviorSubject([]);

  @Input()
  public set dataSource(value: T[]) {
    this.dataSource$.next(value);
  }

  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('dataSource') public dataSourceChange = new EventEmitter<{ from: number; to: number }>();

  @Input() public dragAndDrop = true;
  @Input() public disableDragAndDropTooltipMessage: string;
  @Input() public darkRowBackgroundFn: (data: T) => boolean;
  @Input() public onRowClickFn: (event: MouseEvent, data: T) => void;
  @Input() public trackByFunction: TrackByFunction<T>;
  @Input() public disableMoveToTop = true;
  @Input('data-testid') testId = 'table-view-component';

  @ContentChildren(TableViewCellDirective) public templateChildren: QueryList<TableViewCellDirective>;

  public columnWidth$ = new ReplaySubject<Record<keyof T, string>>(1);
  public columnSortable$ = new ReplaySubject<Record<keyof T, boolean>>(1);
  public displayedColumns$ = new ReplaySubject<Record<keyof T, string>>(1);
  public verticalAlignment$ = new ReplaySubject<Record<keyof T, string>>(1);
  public displayedColumnNames$ = this.displayedColumns$.pipe(map((dc) => Object.keys(dc)));
  public template$ = new ReplaySubject<Record<keyof T, TemplateRef<any>>>(1);

  constructor(private readonly tableViewService: TableViewService) {
    this.darkRowBackgroundFn = () => false;
  }

  public ngAfterContentInit() {
    this.updateColumns();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!this.templateChildren || !changes['dragAndDrop'] || changes['dragAndDrop'].currentValue === changes['dragAndDrop'].previousValue) {
      return;
    }
    this.updateColumns();
  }

  public onCdkDrop($event: CdkDragDrop<T, any>) {
    const newData = this.tableViewService.onCdkDrop(this.dataSource$.value, $event.previousIndex, $event.currentIndex);
    this.dataSource$.next(newData);
    this.dataSourceChange.emit({ from: $event.previousIndex, to: $event.currentIndex });
  }

  public onRowClick(event: MouseEvent, rowData: T): void {
    if (this.onRowClickFn) {
      this.onRowClickFn(event, rowData);
    }
  }

  public moveToTop(event: MouseEvent, data: T) {
    event.stopPropagation();
    const targetIndex = this.dataSource$.value[0]['type'] === 'HeroSpotlight' ? 1 : 0;
    const newData = this.tableViewService.onCdkDrop(this.dataSource$.value, data['order'] - 1, targetIndex);
    this.dataSource$.next(newData);
    this.dataSourceChange.emit({ from: data['order'], to: 0 });
  }

  public onDragHandleClicked(event: MouseEvent) {
    event.stopPropagation();
  }

  private updateColumns(): void {
    const templateChildren = this.templateChildren.toArray();
    this.template$.next(
      // @ts-ignore
      Object.fromEntries(templateChildren.map((t) => [t.cellProperty, t.templateRef]))
    );

    this.columnSortable$.next(
      // @ts-ignore
      Object.fromEntries(templateChildren.map((t) => [t.cellProperty, t.cellSortable]))
    );

    this.displayedColumns$.next(
      /* @ts-ignore*/
      {
        /* @ts-ignore*/
        ...Object.fromEntries(templateChildren.map((t) => [t.cellProperty, t.cellTitle])),
        ...(this.dragAndDrop ? { dragHandle: '' } : {}),
        ...(!this.disableMoveToTop ? { moveToTop: '' } : {}),
      }
    );

    this.columnWidth$.next({
      /* @ts-ignore*/
      ...Object.fromEntries(templateChildren.map((t) => [t.cellProperty, t.cellWidth])),
    });

    this.verticalAlignment$.next({
      /* @ts-ignore*/
      ...Object.fromEntries(templateChildren.map((t) => [t.cellProperty, t.cellVerticalAlignment])),
    });
  }
}
